import { useMemo } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { roundAndDropTrailingZeros } from "@ascendnow/shared";

import AscButton from "../../AscButton/AscButton";
import { AscCalendarEvent } from "../../AscCalendarEvents/AscCalendarEvents.types";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import styles from "./AscEventCard.module.scss";
import AscLink from "../../AscLink/AscLink";
import AscTag from "../../AscTag/AscTag";
import AscTooltip from "../../AscTooltip/AscTooltip";

dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);

const CHARACTER_LIMIT = 36;
const PROGRESS_MULTIPLIER = 4;

export interface AscEventCardProps extends AscCalendarEvent {
  compact?: boolean;
  loading?: boolean;
  eventPath?: string;
  className?: string;
  joined?: boolean;
  isUpcoming?: boolean;
}

export default function AscEventCard({
  title,
  coverImageUrl,
  date,
  type,
  compact = false,
  eventPath,
  loading = false,
  className,
  joined,
  isUpcoming = false,
}: AscEventCardProps) {
  const state = useMemo(
    () =>
      dayjs().isSameOrBefore(dayjs(date)) && isUpcoming ? "upcoming" : "past",
    [date]
  );
  const remainingTimeInHours = dayjs(date).diff(dayjs(), "hours", true);
  const completedPercentage =
    (remainingTimeInHours / 100) * PROGRESS_MULTIPLIER;

  const progressBarStyle = {
    "--value": completedPercentage,
  } as React.CSSProperties;

  return (
    <AscGenericCard
      className={classNames(
        styles.ground,
        {
          [styles.compact]: compact,
        },
        className
      )}
      title={
        title?.length > CHARACTER_LIMIT ? (
          <AscTooltip
            tooltipAnchor={title.slice(0, CHARACTER_LIMIT) + "..."}
            tooltipAnchorClassName={styles.title}
            tooltipContentClassName={styles.titleContent}
          >
            {title}
          </AscTooltip>
        ) : (
          title
        )
      }
      titleClassName={styles.title}
      imageClassName={styles.cardImage}
      coverImageUrl={coverImageUrl}
      tags={
        type
          ? [<AscTag type="custom" color={type?.color} label={type?.name} />]
          : undefined
      }
      bodyClassName={classNames(styles.cardBody, {
        [styles.past]: state === "past",
        [styles.upcoming]: state === "upcoming",
      })}
      tagsContainerClassName={styles.tagsContainer}
      footer={
        state === "upcoming" && (
          <AscLink
            href={eventPath}
            disabled={!eventPath}
            innerWrapperTag={null}
          >
            <AscButton size={compact ? "small" : "medium"} fullWidth>
              Explore
            </AscButton>
          </AscLink>
        )
      }
      footerClassName={styles.footer}
      loading={loading}
    >
      <span className={styles.pastDate}>
        {dayjs(date).format("MMM D, YYYY [at] h:mm A")}
      </span>
      <div className={styles.upcomingTime}>
        <div className={styles.label}>
          {joined ? (
            <div className={styles.upcomingTimeProgress}>
              <div
                className={styles.progressBar}
                role="progressbar"
                aria-valuenow={roundAndDropTrailingZeros(
                  completedPercentage * 10
                )}
                aria-valuemin={0}
                aria-valuemax={100}
                style={progressBarStyle}
              />
              <span className={styles.time}>
                {dayjs(date).toNow(true)} left
              </span>
            </div>
          ) : (
            <span className={styles.long_date}>
              {dayjs(date).format("MMM D, YYYY [at] h:mm A")}
            </span>
          )}
        </div>
      </div>
    </AscGenericCard>
  );
}
